// src/components/ApplicationForms.js
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { getAllApplicationDraftItems, getFormIdsWithDraft } from '../lib/applicationDrafts';
import './ApplicationForms.css';

const forms = [
    { id: 'joined', label: '入社届' },
    { id: 'paid_leave', label: '有給申請届' },
    //{ id: 'remove', label: '異動年月日' },
    { id: 'timeOver', label: '残業・早退・遅刻申請届' },
    { id: 'holiday_work', label: '休日出勤申請届' },
    { id: 'name_change', label: '氏名変更届' },
    { id: 'jisa_kinmu', label: '時差勤務申請' },
    { id: 'bank_change', label: '振込先変更届' },
    { id: 'address_change', label: '住所変更届' },
    { id: 'trains_change', label: '交通機関届' },
    { id: 'family_change', label: '家族異動届' },
    { id: 'mynumber', label: 'マイナンバー届' },
    { id: 'specialLeave', label: '特別休暇届' },
    { id: 'transport_apply', label: '日帰り申請' },
    { id: 'allowance', label: '諸手当' },
    { id: 'retirement', label: '退職届' },
];

const ApplicationForms = () => {
    const navigate = useNavigate();
    const [applicationNotices, setApplicationNotices] = useState([]);
    const [showApplicationNoticeModal, setShowApplicationNoticeModal] = useState(false);
    const [draftTick, setDraftTick] = useState(0);

    const [isFemale, setIsFemale] = useState(false); 
    useEffect(() => {
        const checkIfFemale = async () => {
            try {
                const res = await api.get('/api/employee/me/'); 
                const emp = res.data?.employee || res.data;
                if (emp?.sex === '女性' || emp?.sex === '女') {
                    setIsFemale(true);
                } else {
                    setIsFemale(false);
                }
            } catch (err) {
                console.error("性別の情報を取得できませんでした:", err);
            }
        };
        checkIfFemale();
    }, []);

    const visibleForms = useMemo(() => {
        const list = [...forms];
        if (isFemale) {
            // 女性以外は「生理休暇」を表示しない
            list.splice(2, 0, { id: 'seiri_leave', label: '生理休暇' });
        }
        return list;
    }, [isFemale]);

    useEffect(() => {
        const onDraft = () => setDraftTick((t) => t + 1);
        window.addEventListener('application-draft-saved', onDraft);
        return () => window.removeEventListener('application-draft-saved', onDraft);
    }, []);

    const draftItems = useMemo(() => getAllApplicationDraftItems(), [draftTick]);
    const draftFormIds = useMemo(() => getFormIdsWithDraft(), [draftTick]);

    useEffect(() => {
        // 申請の承認/却下/差し戻しなどを受け取ったら、申請画面で自動的にメッセージを表示
        const titles = new Set([
            '申請を受け付けました',
            '【申請承認のお知らせ】',
            '【申請却下のお知らせ】',
            '【申請差し戻しのお知らせ】',
        ]);
        api.get('/api/notifications/')
            .then((res) => {
                const list = Array.isArray(res.data) ? res.data : [];
                const notices = list.filter((n) => titles.has(n.title));
                setApplicationNotices(notices);
                if (notices.length > 0) setShowApplicationNoticeModal(true);
            })
            .catch(() => {
                setApplicationNotices([]);
            });
    }, []);

    const markApplicationNoticesRead = async () => {
        if (applicationNotices.length === 0) return;
        const withDate = applicationNotices
            .map((n) => ({ n, t: n.created_at ? new Date(n.created_at).getTime() : 0 }))
            .sort((a, b) => b.t - a.t);
        const latest = withDate[0]?.n;
        if (!latest?.id) return;
        try {
            await api.post(`/api/notifications/${latest.id}/mark-read/`);
        } catch (_) {
            // ignore
        }
    };

    const closeModal = async () => {
        setShowApplicationNoticeModal(false);
        await markApplicationNoticesRead();
        setApplicationNotices([]);
    };

    const handleFormSelect = (formId) => {
        navigate(`/selected-forms?ids=${formId}`);
    };

    return (
        <div className="container application-form-container">
            <h3>申請</h3>

            {showApplicationNoticeModal && applicationNotices.length > 0 && (
                <div className="app-notice-overlay" role="dialog" aria-labelledby="app-notice-modal-title" aria-modal="true">
                    <div className="app-notice-modal">
                        <h3 id="app-notice-modal-title" className="app-notice-modal-title">
                            {applicationNotices.some(n => n.title === '【申請却下のお知らせ】')
                                ? '申請未承認のお知らせ'
                                : '申請のお知らせ'}
                        </h3>
                        <div className="app-notice-modal-body">
                            {applicationNotices.map((n, idx) => {
                                const isRejected = n.title === '【申請却下のお知らせ】';
                                const rawContent = n.content || n.message || '';
                                const displayTitle = isRejected ? '【申請未承認のお知らせ】' : n.title;
                                const displayContent = isRejected
                                    ? (() => {
                                        let text = rawContent
                                            .replace('あなたの申請が却下されました。', 'あなたの申請が未承認となりました。');
                                        // 「再申請してください。」を理由行の直後に移動
                                        text = text.replace(/\s*再申請してください。/g, '');
                                        text = text.replace(/(理由：[^\n]*)/, '$1\n再申請してください。');
                                        return text;
                                    })()
                                    : rawContent;
                                return (
                                    <div key={n.id || idx} className="app-notice-item">
                                        <strong className="app-notice-item-title">{displayTitle}</strong>
                                        <p className="app-notice-item-content" style={{ whiteSpace: 'pre-wrap' }}>{displayContent}</p>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="app-notice-modal-actions">
                            <button type="button" className="app-notice-btn-close" onClick={closeModal}>閉じる</button>
                        </div>
                    </div>
                </div>
            )}

            <div className="application-history-link">
                <button type="button" className="btn-link" onClick={() => navigate('/applications/history')}>
                    申請履歴を確認
                </button>
            </div>

            {draftItems.length > 0 && (
                <div className="application-draft-global-summary">
                    <strong>一時保存中の申請</strong>
                    <ul>
                        {draftItems.map((it) => (
                            <li key={it.formId}>
                                <span>{it.title}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="row">
                <div className="col-12">
                    <div className="list-group">
                        {visibleForms.map(form => (
                            <div
                                key={form.id}
                                className="list-group-item list-group-item-action application-form-item"
                                onClick={() => handleFormSelect(form.id)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleFormSelect(form.id);
                                    }
                                }}
                            >
                                <span className="application-form-item-icon">▶</span>
                                <span>{form.label}</span>
                                {draftFormIds.has(form.id) && (
                                    <span className="application-form-draft-badge">一時保存</span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>


            <div className="button-group application-form-button-group">
                <button className="btn-back" onClick={() => navigate('/card_back')}>戻る</button>
            </div>
        </div>
    );
};

export default ApplicationForms;